import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import { Formik, Form, Field } from 'formik';
import { Box, Flex, Text, Image } from 'rebass';
import { useParams } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp, FiCheckCircle } from 'react-icons/fi';

import { getSWR, patch } from 'utils/api';
import { uploadMedium } from 'story-media/upload-medium';
import Loading from 'app/loading';
import TextField from 'app/form/formik/text';
import SelectField from 'app/form/formik/select';
import DateField from 'app/form/formik/date';
import wordmark from 'babys-first-year/assets/babyyearbookslabel.svg';
import { ErrorMessageWrap } from 'app/form/formik/text/styled';
import { FT_COLORS } from '../theme';
import { RELATIONSHIP_OPTIONS } from '../constants';
import { publicFamilyTreeKey, postPublicFamilyMember, uploadFamilyMemberAvatar, uploadFamilyMemberVideo, createdMemberId, createdId, createdLockVersionOfMedia, createdRelationshipId } from '../api';
import { cropParamsFromAttrs, imageUrlFromAttrs, memberFullName, parseFamilyTree, parsePublicInvite } from '../helpers';
import VideoField from '../components/VideoField';
import PhotoField from '../components/PhotoField';
import { PrimaryButton } from '../components/FtButton';
import fishLoading from '../assets/FishLoading.png';
import { useEntity } from 'entities/hooks';
import CroppedAvatar from 'family-tree/components/CroppedAvatar';
import { useWindowSize } from 'react-use';

const STEPS = { INTRO: 0, VIDEO: 1, ABOUT: 2, PROCESSING: 3, SUCCESS: 4 };

const RECORDING_TIPS = [
  'Keep your phone steady',
  'Record somewhere quiet',
  'Face a window for soft, even light',
  'Aim for 30–90 seconds',
];

const fieldLabel = (text, required) => (
  <Text sx={{ color: 'codGrey', fontFamily: 'Poppins', fontSize: '14px', fontWeight: 500, lineHeight: '20px' }}>
    {text}
    {required && <span style={{ color: 'red' }}> *</span>}
  </Text>
);

const TopicsBox = ({ topics }) => (
  topics.length ? (
    <Box my={3} p={3} bg="#D9EAF3" sx={{ borderRadius: '10px' }}>
      <Text fontFamily="Poppins" fontWeight={600} fontSize="14px" color={FT_COLORS.text} mb={2}>
        💡 What to say in your video
      </Text>
      {topics.map((t, i) => (
        <Text key={t} fontFamily="Poppins" fontSize="13px" pb={i !== topics.length - 1 ? 1 : 0} color={FT_COLORS.muted} mb={i !== topics.length - 1 ? 1 : 0}
          sx={i !== topics.length - 1 && {
            borderBottom: '1px solid #CACFD8'
          }}>
          {t}
        </Text>
      ))}
    </Box >
  ) : null
);
TopicsBox.propTypes = { topics: PropTypes.arrayOf(PropTypes.string).isRequired };

// ── Step: intro ──────────────────────────────────────────────────────────────
const IntroStep = ({ babyName, invitationCopy, topics, avatarRelationShip }) => {
  const { entity } = useEntity(avatarRelationShip)

  return (
    <Box>
      <Flex bg={FT_COLORS.surface} p={2} alignItems="center" sx={{ gap: '10px', borderRadius: '10px' }} mb={3}>
        <CroppedAvatar
          url={imageUrlFromAttrs(entity)}
          name={memberFullName({ firstName: babyName })}
          size={40}
          circular
          {...cropParamsFromAttrs(entity)}
        />
        <Text fontFamily="Bitter" fontWeight={500} fontSize="16px" color={FT_COLORS.text}>
          {`Be part of ${babyName}'s Talking Family Tree`}
        </Text>
      </Flex>
      <Text fontFamily="Poppins" fontSize="14px" color={FT_COLORS.text} css="white-space: pre-wrap;">
        {invitationCopy || `Hi! I'm adding a Talking Family Tree to ${babyName}'s Baby Yearbook. I'd love for you to be in it!`}
      </Text>

      <TopicsBox topics={topics} />

      <Box mt={3} p={3} bg="#26303F" sx={{ borderRadius: '10px' }}>
        <Text fontFamily="Poppins" fontWeight={600} fontSize="14px" color="#fff" mb={2}>
          🎬 Recording Tips
        </Text>
        {RECORDING_TIPS.map((tip) => (
          <Text key={tip} fontFamily="Poppins" fontSize="13px" color="rgba(255,255,255,0.85)" mb={1}>
            • {tip}
          </Text>
        ))}
      </Box>
    </Box>
  )
};
IntroStep.propTypes = {
  babyName: PropTypes.string.isRequired,
  invitationCopy: PropTypes.string,
  topics: PropTypes.arrayOf(PropTypes.string).isRequired,
};
IntroStep.defaultProps = { invitationCopy: '' };

// ── Step: add your video ───────────────────────────────────────────────────────
const VideoStep = ({ topics }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Text fontFamily="Bitter" fontWeight={500} fontSize="30px" color={FT_COLORS.text} mb={3}>
        Add your video
      </Text>
      <Flex flexDirection="column" alignItems="flex-start" p="10px" sx={{
        gap: '8px',
        background: 'linear-gradient(90deg, #F3E8FF 0%, #FCE7F3 100%)',
        borderRadius: '10px'
      }} mb={3}>
        <Text fontFamily="Poppins" fontWeight={600} fontSize="14px" color={FT_COLORS.text} mb={2}>
          👤 One person, one profile
        </Text>
        <Text fontFamily="Poppins" fontSize="13px" color={FT_COLORS.muted} >
          Please record a video featuring only yourself. Other family members can use the same link to add
          their own profile and video.
        </Text>
      </Flex>

      <TopicsBox topics={topics} />

      <Field name="video" component={VideoField} label="" />
    </Box>
  );
};
VideoStep.propTypes = { topics: PropTypes.arrayOf(PropTypes.string).isRequired };

// ── Step: about you ────────────────────────────────────────────────────────────
const AboutStep = ({ babyName, parentCantBeAdded }) => (
  <Box>
    <Text fontFamily="Bitter" fontWeight={500} fontSize="30px" color={FT_COLORS.text} mb={1}>
      About You
    </Text>
    <Text fontFamily="Poppins" fontSize="13px" color={FT_COLORS.muted} mb={3}>
      {`Now a few details so ${babyName} knows who this wonderful message is from.`}
    </Text>
    <Box css="display: grid; grid-gap: 14px;">
      <Field component={TextField} name="first-name" label={fieldLabel('First Name', true)} placeholder="First Name" showError />
      <Field component={TextField} name="personal-name" label={fieldLabel(`What will ${babyName} call you`)} placeholder="ex. Auntie Liz" showError />
      <Field
        component={SelectField}
        name="relationship"
        label={fieldLabel(`Relationship to ${babyName}`, true)}
        options={RELATIONSHIP_OPTIONS} placeholder="Select a relationship"
        showError
      />
      {parentCantBeAdded && <ErrorMessageWrap sx={{ color: 'red', fontFamily: 'Poppins', fontSize: '13px' }}>Maximum parent limit reached. Please select a different relationship.</ErrorMessageWrap>}
      <Field component={DateField} name="birthdate" label={fieldLabel('Birthdate', true)} showError />
      <Field name="avatar" component={PhotoField} label={fieldLabel("Your Profile Photo", true)} circular />
      <Text fontFamily="Poppins" fontSize="12px" color={FT_COLORS.muted}>
        {`This will be on ${babyName}'s family tree page`}
      </Text>
    </Box>
  </Box>
);
AboutStep.propTypes = { babyName: PropTypes.string.isRequired, parentCantBeAdded: PropTypes.bool };

// Upload the recipient's media (original photo + crop params, and the video),
// reusing the shared media endpoints.
//
// Returns the list of media that failed to upload, rather than throwing. The
// recipient's profile has already been created by the time this runs, so a failed
// photo used to abort the video upload and the crop PATCH as well, and drop the
// whole submit into the generic "Something went wrong" branch -- sending the person
// back to the form to re-enter a profile that had in fact been saved.
const uploadRecipientMedia = async (memberId, avatar, video, lockVersion, relationshipId) => {
  const uploadFailures = [];
  const fileBlob = avatar instanceof Blob ? avatar : (avatar && avatar.file) || null;
  if (fileBlob instanceof Blob) {
    const { file, ...croppingParams } = avatar
    const res = await uploadFamilyMemberAvatar(memberId, file.name || 'avatar.jpg');
    const medium = res?.data?.entities?.images?.[res?.data?.result?.images?.[0]];
    if (medium?.['upload-url']) {
      const upload = await uploadMedium({ medium, mediumType: 'Image', file });
      if (!upload.ok) uploadFailures.push('photo');
    }
    if (medium?.id && avatar && !(avatar instanceof Blob)) {
      await patch(`/v2/media/${medium.id}`, {
        data: {
          attributes: {
            'lock-version': lockVersion,
            ...croppingParams
          }
        },
      });
    }
  }
  if (video instanceof Blob) {

    const res = await uploadFamilyMemberVideo(relationshipId, video.name || 'video.webm');

    const medium = res?.data?.entities?.videos?.[res?.data?.result?.videos?.[0]];
    if (medium?.['upload-url']) {
      const upload = await uploadMedium({ medium, mediumType: 'Video', file: video });
      if (!upload.ok) uploadFailures.push('video');
    }
  }
  return uploadFailures;
};

// ── The recipient flow ─────────────────────────────────────────────────────────
const RecipientInviteFlow = ({ slug, babyName, invitationCopy, topics, avatarRelationShip, parentLimitReached }) => {
  const [step, setStep] = useState(STEPS.INTRO);
  const [submitError, setSubmitError] = useState(null);
  const { width } = useWindowSize();

  const { entity } = useEntity(avatarRelationShip)

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step])

  const onSubmit = async (values) => {
    setSubmitError(null);

    setStep(STEPS.PROCESSING);
    const { avatar, video, ...rest } = values;
    const attributes = {
      'first-name': rest['first-name'],
      'personal-name': rest['personal-name'] || undefined,
      'relationship-kind': rest.relationship,
      'date-of-birth': rest.birthdate || undefined,
    };
    try {
      // TODO(api): confirm the public recipient submit contract.
      const res = await postPublicFamilyMember(slug, attributes);
      const memberId = createdMemberId(res) || createdId(res);
      const locakVersion = createdLockVersionOfMedia(res)
      const relationshipId = createdRelationshipId(res);

      const uploadFailures = memberId
        ? await uploadRecipientMedia(memberId, avatar, video, locakVersion, relationshipId)
        : [];

      // The profile is saved either way, so the success step is correct. Say what
      // is missing rather than sending them back to re-enter everything.
      if (uploadFailures.length) {
        setSubmitError(
          `You're added, but your ${uploadFailures.join(' and ')} could not be uploaded. ` +
          'You can use your invite link again to add it.'
        );
      }
      setStep(STEPS.SUCCESS);
    } catch (e) {
      setSubmitError('Something went wrong. Please try again.');
      setStep(STEPS.ABOUT);
    }
  };

  return (
    <Formik
      initialValues={{ 'first-name': '', 'personal-name': '', relationship: '', birthdate: '', avatar: '', video: '' }}
      onSubmit={onSubmit}
    >
      {(formik) => {
        const v = formik.values;
        const hasVideo = v.video instanceof Blob || (v.video && v.video.name);
        const aboutValid = v['first-name']?.trim() && v.relationship && v.birthdate
          && (v.avatar instanceof Blob || (v.avatar && v.avatar.file));
        const parentCantBeAdded = parentLimitReached && (formik?.values?.relationship === 'mother' || formik?.values?.relationship === 'father')

        if (step === STEPS.PROCESSING) {
          return (
            <Flex flexDirection="column" alignItems="center" justifyContent="flex-start" py={6} px={3} css="flex: 1 1 auto;">
              <Image src={fishLoading} height={170} mb={2} />
              <Text fontFamily="Bitter" fontWeight={700} fontSize="30px" color="#000" textAlign="center" mb={4}>
                Please stay on this page while we finish processing your video
              </Text>
              <Loading local />
              <Text fontFamily="Poppins" fontSize="13px" color={FT_COLORS.muted} mt={3}>
                Uploading media...
              </Text>
            </Flex>
          );
        }

        if (step === STEPS.SUCCESS) {
          return (
            <Flex flexDirection="column" alignItems="center" textAlign="center" py={6} px={3} css="flex: 1 1 auto;">
              <CroppedAvatar
                url={imageUrlFromAttrs(entity)}
                name={memberFullName({ firstName: babyName })}
                size={160}
                circular
                {...cropParamsFromAttrs(entity)}
              />
              <Text fontFamily="Bitter" fontWeight={500} fontSize="22px" color={FT_COLORS.text} mb={2}>
                {`Woohoo 🎉 Thanks ${v['first-name'] || ''}. You are a part of ${babyName}'s Talking Family Tree!`}
              </Text>
              <Text fontFamily="Poppins" fontSize="13px" color={FT_COLORS.muted} mb={4}>
                {`Your video and profile have been added to ${babyName}'s Talking Family Tree. ${babyName}'s family will see it the moment they scan the book.`}
              </Text>
              <Flex alignItems="center" sx={{ gap: '6px', borderRadius: '100px', bg: FT_COLORS.successBg, color: FT_COLORS.successText }} px={3} py={1}>
                <FiCheckCircle size={16} />
                <Text fontFamily="Poppins" fontSize="13px" fontWeight={500}>Video Uploaded</Text>
              </Flex>
            </Flex>
          );
        }

        return (
          <Form autoComplete="off" style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
            <Box flex="1 1 auto" p={3} css="overflow-y: auto;" pb={[9, '80px']}>
              {step === STEPS.INTRO && <IntroStep babyName={babyName} avatarRelationShip={avatarRelationShip} invitationCopy={invitationCopy} topics={topics} />}
              {step === STEPS.VIDEO && <VideoStep topics={topics} />}
              {step === STEPS.ABOUT && <AboutStep parentCantBeAdded={parentCantBeAdded} babyName={babyName} />}
              {submitError && (
                <Text color="red" fontFamily="Poppins" fontSize="13px" mt={2}>{submitError}</Text>
              )}
            </Box>

            <Flex px={3} py={3} alignItems="center" justifyContent="space-between" sx={{
              borderTop: '1px solid', borderColor: FT_COLORS.rowBorder,
              ...(width < 500 && {
                position: 'fixed', bottom: 0, left: '50%', right: 0, backgroundColor: '#fff', zIndex: 1,
                transform: 'translate(-50%, 0%)',
                maxWidth: '480px', width: '100%'
              })
            }}>
              {step === STEPS.INTRO ? <Box /> : (
                <Box
                  as="button"
                  type="button"
                  onClick={() => setStep((s) => s - 1)}
                  sx={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', color: FT_COLORS.text, fontFamily: 'Poppins', fontSize: '14px' }}
                >
                  <FiChevronLeft size={16} /> Back
                </Box>
              )}

              {step === STEPS.INTRO && (
                <PrimaryButton onClick={() => setStep(STEPS.VIDEO)}>Get Started <FiChevronRight /></PrimaryButton>
              )}
              {step === STEPS.VIDEO && (
                <PrimaryButton onClick={() => setStep(STEPS.ABOUT)}>Next <FiChevronRight /></PrimaryButton>
              )}
              {step === STEPS.ABOUT && (
                <PrimaryButton disabled={!aboutValid || formik.isSubmitting || parentCantBeAdded} loading={formik.isSubmitting} onClick={formik.submitForm}>
                  Next <FiChevronRight />
                </PrimaryButton>
              )}
            </Flex>
          </Form>
        );
      }}
    </Formik>
  );
};
RecipientInviteFlow.propTypes = {
  slug: PropTypes.string.isRequired,
  babyName: PropTypes.string.isRequired,
  invitationCopy: PropTypes.string,
  topics: PropTypes.arrayOf(PropTypes.string).isRequired,
};
RecipientInviteFlow.defaultProps = { invitationCopy: '' };

// ── Route container: /tft/:slug ────────────────────────────────────────────────
const RecipientInvitePage = () => {
  const { slug } = useParams();
  const { data, error } = useSWR(publicFamilyTreeKey(slug), getSWR, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const loading = !data && !error;
  const invite = useMemo(() => parsePublicInvite(data), [data]);
  const { members, groups } = parseFamilyTree(data);

  const parentLimitReached = groups.find((group) => group.key === 'parents')?.members?.length >= 4

  return (
    <Flex css="min-height: 100vh;" justifyContent="center" alignItems="flex-start" bg="#ECECEC" p={[0, 3, 4]}>
      <Flex
        flexDirection="column"
        width="100%"
        maxWidth={480}
        bg="white"
        sx={{ borderRadius: ['0px', '16px'], boxShadow: 'container', minHeight: ['100vh', '86vh'], overflow: 'hidden' }}
      >
        <Flex alignItems="center" px={3} py={2} sx={{ borderBottom: '1px solid', borderColor: FT_COLORS.rowBorder }}>
          <Image src={wordmark} alt="Baby Yearbooks" maxWidth={140} />
        </Flex>

        {loading && (
          <Flex flex="1 1 auto" alignItems="center" justifyContent="center" py={6}>
            <Loading local />
          </Flex>
        )}
        {error && (
          <Flex flex="1 1 auto" alignItems="center" justifyContent="center" py={6} px={3}>
            <Text fontFamily="Poppins" fontSize="14px" color={FT_COLORS.muted} textAlign="center">
              This invitation is no longer available.
            </Text>
          </Flex>
        )}
        {!loading && !error && (
          <RecipientInviteFlow slug={slug}
            parentLimitReached={parentLimitReached} babyName={invite.babyName} invitationCopy={invite.invitationCopy} topics={invite.topics}
            avatarRelationShip={invite?.avatarRelationShip}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default RecipientInvitePage;
